import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/base/Navigation';
import Footer from '../../components/feature/Footer';

// Animation Hook
const useScrollAnimation = () => {
  const [visibleElements, setVisibleElements] = useState(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleElements(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return visibleElements;
};

export default function Services() {
  const visibleElements = useScrollAnimation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const heroImages = [
    {
      url: 'https://public.readdy.ai/ai/img_res/a96c1b4d99c19f0e9a34ca1e9c21b72d.jpg',
      alt: 'Professional Corporate Office'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Authentic%20coworking%20space%20with%20diverse%20professionals%20working%20at%20wooden%20communal%20tables%20MacBooks%20and%20coffee%20cups%20scattered%20around%20large%20industrial%20windows%20with%20natural%20daylight%20exposed%20brick%20walls%20modern%20pendant%20lighting%20real%20candid%20office%20photography%20people%20collaborating%20genuine%20workplace%20atmosphere%20contemporary%20shared%20workspace&width=1920&height=600&seq=services-real-cowork-space-1&orientation=landscape',
      alt: 'Contemporary Coworking Space'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Luxury%20executive%20corner%20office%20with%20panoramic%20floor%20to%20ceiling%20windows%20showcasing%20metropolitan%20skyline%20view%20premium%20leather%20executive%20chair%20polished%20mahogany%20desk%20with%20laptop%20and%20business%20documents%20golden%20hour%20natural%20lighting%20high%20end%20commercial%20real%20estate%20photography%20sophisticated%20professional%20business%20interior%20architectural%20photography%20style&width=1920&height=600&seq=services-real-exec-suite-1&orientation=landscape',
      alt: 'Executive Office Suite'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Contemporary%20tech%20startup%20office%20with%20open%20floor%20plan%20software%20developers%20at%20standing%20desks%20multiple%20computer%20monitors%20displaying%20code%20exposed%20brick%20walls%20industrial%20style%20lighting%20real%20workplace%20photography%20authentic%20creative%20agency%20environment%20modern%20collaborative%20workspace%20natural%20candid%20business%20setting&width=1920&height=600&seq=services-real-tech-startup-1&orientation=landscape',
      alt: 'Tech Startup Office'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Professional%20modern%20conference%20room%20with%20long%20polished%20table%20and%20ergonomic%20chairs%20large%20presentation%20screen%20mounted%20on%20wall%20floor%20to%20ceiling%20windows%20with%20natural%20daylight%20sleek%20contemporary%20design%20real%20commercial%20interior%20photography%20business%20meeting%20space%20architectural%20photography%20style%20authentic%20corporate%20boardroom&width=1920&height=600&seq=services-real-conference-rm-1&orientation=landscape',
      alt: 'Conference Room'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Elegant%20corporate%20office%20lobby%20with%20modern%20reception%20desk%20comfortable%20designer%20seating%20area%20large%20windows%20flooding%20space%20with%20natural%20light%20indoor%20tropical%20plants%20contemporary%20furniture%20real%20architectural%20photography%20welcoming%20professional%20business%20entrance%20high%20end%20commercial%20interior%20authentic%20corporate%20reception%20area&width=1920&height=600&seq=services-real-lobby-entrance-1&orientation=landscape',
      alt: 'Office Lobby'
    }
  ];

  useEffect(() => {
    const getRandomInterval = () => Math.floor(Math.random() * 3000) + 4000; // 4-7 seconds
    
    const changeImage = () => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
        setIsTransitioning(false);
      }, 500);
    };

    const intervalId = setInterval(changeImage, getRandomInterval());
    
    return () => clearInterval(intervalId);
  }, [heroImages.length]);

  const goToImage = (index: number) => {
    if (index !== currentImageIndex) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentImageIndex(index);
        setIsTransitioning(false);
      }, 500);
    }
  };

  const getAnimationClass = (elementId: string, baseClass: string = '') => {
    const isVisible = visibleElements.has(elementId);
    return `${baseClass} transition-all duration-1000 ease-out ${
      isVisible
        ? 'opacity-100 translate-y-0 scale-100'
        : 'opacity-0 translate-y-8 scale-95'
    }`;
  };

  const services = [
    {
      icon: 'ri-window-line',
      title: 'Website Design & Development',
      description: 'Custom-designed websites built specifically for your business. We create modern, professional sites that reflect your brand and help you connect with customers.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20UX%20UI%20designer%20working%20at%20pristine%20Apple%20iMac%20displaying%20beautiful%20website%20homepage%20mockup%20with%20clear%20English%20text%20headings%20navigation%20menu%20hero%20section%20and%20call%20to%20action%20buttons%20visible%20Adobe%20XD%20Figma%20artboards%20open%20on%20secondary%20monitor%20showing%20wireframes%20and%20color%20palette%20swatches%20organized%20modern%20desk%20with%20Wacom%20tablet%20wireless%20keyboard%20mouse%20and%20coffee%20cup%20bright%20contemporary%20office%20with%20floor%20to%20ceiling%20windows%20natural%20daylight%20streaming%20in%20ultra%20sharp%20focus%20crystal%20clear%208K%20professional%20photography%20highest%20quality%20realistic%20workplace%20environment&width=600&height=400&seq=service-webdesign-english-v3&orientation=landscape',
      features: [
        'Custom design tailored to your brand',
        'Mobile-responsive on all devices',
        'Fast loading speeds',
        'Easy content management',
        'Secure hosting included'
      ]
    },
    {
      icon: 'ri-robot-line',
      title: 'AI Add-Ons & Automation',
      description: 'Enhance your website with intelligent AI tools that engage customers 24/7. Our AI voice agents and chatbots handle inquiries, book appointments, and provide instant support.',
      image: 'https://readdy.ai/api/search-image?query=Modern%20computer%20screen%20displaying%20artificial%20intelligence%20chatbot%20interface%20with%20conversation%20bubbles%20and%20voice%20waveform%20visualization%20neural%20network%20graphics%20glowing%20on%20secondary%20monitor%20professional%20tech%20office%20workspace%20with%20ambient%20lighting%20real%20workplace%20photography%20authentic%20AI%20technology%20development%20environment%20contemporary%20software%20engineering%20setup&width=600&height=400&seq=service-ai-tech-real&orientation=landscape',
      features: [
        'AI Voice Agents for phone support',
        'Intelligent chatbots for instant responses',
        'Automated appointment scheduling',
        '24/7 customer engagement',
        'Natural language understanding',
        'Seamless website integration'
      ]
    },
    {
      icon: 'ri-refresh-line',
      title: 'Website Updates & Maintenance',
      description: 'Need to swap out a photo, update your hours, or change some wording? We handle all content updates for you so your website always reflects the latest information about your business.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20person%20updating%20website%20content%20on%20laptop%20at%20clean%20modern%20desk%20with%20coffee%20cup%20editing%20text%20photos%20and%20wording%20on%20a%20business%20website%20homepage%20bright%20contemporary%20home%20office%20setting%20natural%20daylight%20warm%20tones%20authentic%20candid%20workplace%20photography&width=600&height=400&seq=service-content-updates-v2&orientation=landscape',
      features: [
        'Text and wording changes',
        'Photo and image swaps',
        'Business hours and info updates',
        'Menu or service list changes',
        'Fast turnaround on requests'
      ]
    }
  ];

  const pricing = [
    {
      name: 'Starter',
      price: '$1,000',
      period: 'One-Time + $49/mo Hosting',
      description: 'Perfect for small businesses getting started online',
      features: [
        'Custom Website Design',
        'Mobile Friendly',
        'Up to 3 Pages',
        'Contact Form',
        'Optional $75/mo Hosting + Updates (Text/Photos/Wording)',
        'Optional $150/mo Hosting + Updates + All Add-Ons Access'
      ],
      popular: false
    },
    {
      name: 'Business',
      price: '$1,600',
      period: 'One-Time + $25/mo Hosting',
      description: 'Complete website solution with flexible monthly options',
      features: [
        'Everything in Starter',
        '4-6 Pages',
        'Contact Form',
        'Social Media Links',
        'Optional $75/mo Hosting + Updates (Text/Photos/Wording)',
        'Optional $150/mo Hosting + Updates + All Add-Ons Access'
      ],
      popular: false
    },
    {
      name: 'Premium',
      price: '$2,500',
      period: 'One-Time, No Monthly Fees',
      description: 'Complete premium package with everything included',
      features: [
        'Up to 7 Pages',
        'Everything in Business + $75/mo Package',
        'Unlimited Content Updates',
        'Priority Support',
        'No Monthly Fees Ever'
      ],
      popular: true
    }
  ];

  const addOns = [
    {
      name: 'AI Chatbots',
      description: 'Intelligent chatbots for instant customer support and engagement'
    },
    {
      name: 'AI Voice Agents',
      description: 'Voice-powered customer service and automated phone support'
    },
    {
      name: 'Automated Appointment Scheduling',
      description: 'Smart calendar integration and comprehensive management automation'
    },
    {
      name: 'Natural Language Processing',
      description: 'Advanced conversational AI with sophisticated understanding capabilities'
    },
    {
      name: '24/7 Customer Engagement',
      description: 'Round-the-clock automated customer interaction and support'
    },
    {
      name: 'Professional Brand Identity',
      description: 'AI-generated professional logo design with comprehensive brand package'
    },
    {
      name: 'Strategic Content Development',
      description: 'Professional copywriting and content strategy for all digital touchpoints'
    },
    {
      name: 'E-commerce Platform Integration',
      description: 'Complete online commerce solution with advanced payment processing systems'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden">
      {/* Futuristic background effects */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-500/20 via-indigo-600/10 to-transparent"></div>
        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-gradient-to-tr from-indigo-600/30 to-transparent blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>
      
      <Navigation />
      
      {/* Hero Section with Rotating Background */}
      <section className="pt-24 pb-16 relative overflow-hidden">
        {/* Background Images Container */}
        <div className="absolute inset-0">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
                index === currentImageIndex
                  ? 'opacity-100 scale-100 blur-0'
                  : 'opacity-0 scale-110 blur-sm'
              }`}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
          
          {/* Gradient Overlays */}
          <div className={`absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-slate-950 transition-opacity duration-1000 ${
            isTransitioning ? 'opacity-80' : 'opacity-70'
          }`}></div>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 to-indigo-900/20"></div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div 
            id="services-hero" 
            data-animate 
            className={getAnimationClass('services-hero', 'text-center space-y-8')}
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white font-space">
              Our <span className="text-indigo-400">Services</span>
            </h1>
            <p className="text-xl text-slate-200 max-w-4xl mx-auto leading-relaxed">
              Comprehensive website solutions designed to help your business grow. From initial design 
              to ongoing support, we provide everything you need for online success.
            </p>
          </div>
        </div>

        {/* Image Indicators */}
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => goToImage(index)}
              className={`transition-all duration-300 rounded-full cursor-pointer ${
                index === currentImageIndex
                  ? 'w-8 h-2 bg-indigo-400'
                  : 'w-2 h-2 bg-white/50 hover:bg-white/80'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {services.slice(0, 2).map((service, index) => (
              <div 
                key={index}
                id={`service-${index}`}
                data-animate 
                className={getAnimationClass(`service-${index}`, 'bg-slate-800/50 rounded-xl overflow-hidden border border-slate-700/50 hover:border-indigo-500/50 transition-all duration-300')}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Service Image */}
                <div className="relative w-full h-64 overflow-hidden group">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent"></div>
                </div>

                {/* Service Content */}
                <div className="p-8">
                  <div className="w-16 h-16 bg-indigo-500/20 rounded-xl flex items-center justify-center mb-6">
                    <i className={`${service.icon} text-indigo-400 text-3xl`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 font-space">{service.title}</h3>
                  <p className="text-slate-300 mb-6 leading-relaxed">{service.description}</p>
                  <ul className="space-y-3">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start text-slate-300">
                        <i className="ri-check-line text-indigo-400 mr-3 mt-1 flex-shrink-0"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Centered third service card */}
          <div className="flex justify-center mt-8">
            <div 
              id="service-2"
              data-animate 
              className={getAnimationClass('service-2', 'bg-slate-800/50 rounded-xl overflow-hidden border border-slate-700/50 hover:border-indigo-500/50 transition-all duration-300 w-full md:w-1/2')}
              style={{ animationDelay: '0.2s' }}
            >
              {/* Service Image */}
              <div className="relative w-full h-64 overflow-hidden group">
                <img 
                  src={services[2].image} 
                  alt={services[2].title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent"></div>
              </div>

              {/* Service Content */}
              <div className="p-8">
                <div className="w-16 h-16 bg-indigo-500/20 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${services[2].icon} text-indigo-400 text-3xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 font-space">{services[2].title}</h3>
                <p className="text-slate-300 mb-6 leading-relaxed">{services[2].description}</p>
                <ul className="space-y-3">
                  {services[2].features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start text-slate-300">
                      <i className="ri-check-line text-indigo-400 mr-3 mt-1 flex-shrink-0"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            id="pricing-header" 
            data-animate 
            className={getAnimationClass('pricing-header', 'text-center mb-16')}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-space">
              Simple, <span className="text-indigo-400">Transparent Pricing</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Clear pricing with no hidden fees. Choose the package that fits your business needs.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricing.map((plan, index) => (
              <div 
                key={index}
                id={`plan-${index}`}
                data-animate 
                className={getAnimationClass(`plan-${index}`, `relative bg-slate-900/60 backdrop-blur-xl rounded-2xl p-8 border transition-all duration-300 hover:-translate-y-2 ${
                  plan.popular 
                    ? 'border-blue-500 shadow-xl shadow-blue-500/20 scale-105' 
                    : 'border-slate-700/50 hover:border-blue-500/50 shadow-lg shadow-blue-500/10'
                }`)}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm font-semibold px-4 py-2 rounded-full whitespace-nowrap shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-white mb-2 bg-gradient-to-r from-blue-400 to-indigo-500 bg-clip-text text-transparent">{plan.name}</h3>
                  <p className="text-slate-400 mb-6">{plan.description}</p>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-white">
                      {plan.price}
                    </span>
                    <div className="text-slate-400 text-sm mt-1">
                      {plan.period}
                    </div>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-slate-300">
                      <i className="ri-check-line text-blue-400 mr-3 mt-0.5 flex-shrink-0 text-base"></i>
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  className={`w-full py-4 px-6 rounded-lg font-semibold transition-all duration-300 text-center block whitespace-nowrap cursor-pointer shadow-lg hover:-translate-y-0.5 ${
                    plan.popular
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 shadow-blue-500/25'
                      : 'bg-slate-700/80 text-white hover:bg-slate-700 border border-slate-600/50'
                  }`}
                >
                  {plan.name}
                </Link>
              </div>
            ))}
          </div>

          {/* AI Add-Ons - Horizontal Layout */}
          <div 
            id="plan-addons"
            data-animate 
            className={getAnimationClass('plan-addons', 'relative bg-gradient-to-r from-indigo-900/40 via-slate-900/60 to-indigo-900/40 backdrop-blur-xl rounded-2xl p-10 border border-indigo-500/30 transition-all duration-300 hover:-translate-y-1 shadow-lg shadow-indigo-500/10 max-w-6xl mx-auto mt-12')}
          >
            <div className="flex flex-col lg:flex-row lg:items-center gap-8">
              {/* Left - Title & CTA */}
              <div className="lg:w-1/3 text-center lg:text-left flex-shrink-0">
                <div className="w-14 h-14 bg-indigo-500/20 rounded-full flex items-center justify-center mx-auto lg:mx-0 mb-4">
                  <i className="ri-robot-line text-indigo-400 text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2 bg-gradient-to-r from-indigo-400 to-blue-500 bg-clip-text text-transparent">AI Add-Ons</h3>
                <p className="text-slate-400 mb-4 text-sm">Enhance your website with intelligent automation and professional services</p>
                <div className="mb-5">
                  <span className="text-xl font-bold text-white">Custom Pricing</span>
                  <div className="text-slate-400 text-xs mt-1">Based on sophistication level</div>
                </div>
                <Link
                  to="/contact"
                  className="inline-block py-3 px-8 rounded-lg font-semibold transition-all duration-300 text-center whitespace-nowrap cursor-pointer shadow-lg hover:-translate-y-0.5 bg-gradient-to-r from-indigo-500 to-blue-600 text-white hover:from-indigo-600 hover:to-blue-700 shadow-indigo-500/25 text-sm"
                >
                  Get Custom Quote
                </Link>
              </div>

              {/* Right - Add-Ons Grid */}
              <div className="lg:w-2/3">
                <div className="grid sm:grid-cols-2 gap-4">
                  {addOns.map((addon, idx) => (
                    <div key={idx} className="flex items-start text-slate-300 bg-slate-800/40 rounded-lg p-4 border border-slate-700/30">
                      <i className="ri-check-line text-indigo-400 mr-3 mt-0.5 flex-shrink-0 text-base"></i>
                      <div>
                        <span className="text-sm font-semibold text-white block">{addon.name}</span>
                        <span className="text-xs text-slate-400">{addon.description}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-900/50 relative z-10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div 
            id="faq-header" 
            data-animate 
            className={getAnimationClass('faq-header', 'text-center mb-16')}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-space">
              Frequently Asked <span className="bg-gradient-to-r from-blue-400 to-indigo-500 bg-clip-text text-transparent">Questions</span>
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                question: "What's included in the Starter package?",
                answer: "The Starter package is $1,000 one-time plus $49/month for hosting. It includes a custom website design, mobile-friendly layout, up to 3 pages, and a contact form. You can optionally upgrade to $75/month for hosting + content updates (text/photos/wording) or $150/month for hosting + updates + all AI add-ons access."
              },
              {
                question: "What's included in the Business package?",
                answer: "The Business package is $1,600 one-time plus $25/month for hosting. It includes everything in Starter, plus 4-6 custom pages, contact form, and social media links. You can optionally upgrade to $75/month for hosting + content updates (text/photos/wording) or $150/month for hosting + updates + all AI add-ons access."
              },
              {
                question: "What's the difference between the Business and Premium packages?",
                answer: "The Business package ($1,600 + monthly hosting) offers flexibility with optional monthly services. The Premium package ($2,500 all-in) includes up to 7 pages, everything from Business plus the $75/month package features—unlimited content updates and priority support—with no monthly fees ever."
              },
              {
                question: "How long does it take to build my website?",
                answer: "Most websites are completed within 3-5 business days. More complex projects with custom features or extensive content may take 7-10 business days. We'll provide you with a specific timeline during our initial consultation."
              },
              {
                question: "Can I upgrade my hosting plan later?",
                answer: "Absolutely! You can start with basic hosting and upgrade to plans that include content updates at any time. For example, both Starter and Business customers can upgrade to $75/month or $150/month plans. You can also add AI Add-Ons whenever you're ready to enhance your website's capabilities."
              },
              {
                question: "Do you serve businesses outside of Buffalo, NY?",
                answer: "Yes! While we're based in Buffalo, NY, we work with businesses throughout the United States. All our services are available remotely, and we use modern collaboration tools to ensure seamless communication."
              },
              {
                question: "How does pricing work for AI Add-Ons?",
                answer: "AI Add-Ons like chatbots, voice agents, and automated scheduling are priced based on sophistication level and your specific needs. We'll discuss your requirements and provide a custom quote. These can be added to any package at any time, or you can access all add-ons with the $150/month plan available for both Starter and Business packages."
              }
            ].map((faq, index) => (
              <div 
                key={index}
                id={`faq-${index}`}
                data-animate 
                className={getAnimationClass(`faq-${index}`, 'bg-slate-900/60 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50 hover:border-blue-500/30 transition-all duration-300')}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <h3 className="text-lg font-semibold text-white mb-3 bg-gradient-to-r from-blue-400 to-indigo-500 bg-clip-text text-transparent">{faq.question}</h3>
                <p className="text-slate-300 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
